/**
 * Realtime frames for the code surfaces.
 *
 *   code.connections.changed   (tenant)  a connection or its repository list
 *                                        changed (connect, check, refresh,
 *                                        disconnect, GitHub webhook)
 *   project.repositories.changed (project) the repositories linked to a
 *                                        project changed
 *
 * Card changes caused by either (a cleared Repository field) already arrive
 * as ordinary card frames, so these only refresh the code-specific queries
 * plus the board and card reads that carry the repository chip.
 */

import type { QueryClient } from '@tanstack/react-query'

import { DEEP_LINK_CARD_QUERY_PREFIX } from '@/features/board/use-deep-link-card'

import { CODE_CONNECTIONS_KEY, CODE_REPOSITORIES_ROOT, projectRepositoriesKey } from './query-keys'

export const CODE_CONNECTIONS_FRAME = 'code.connections.changed'
export const PROJECT_REPOSITORIES_FRAME = 'project.repositories.changed'

/** True when the frame belongs to this module (the caller then stops dispatching). */
export function handleCodeFrame(queryClient: QueryClient, type: string, payload: unknown): boolean {
  if (type === CODE_CONNECTIONS_FRAME) {
    void queryClient.invalidateQueries({ queryKey: CODE_CONNECTIONS_KEY })
    void queryClient.invalidateQueries({ queryKey: CODE_REPOSITORIES_ROOT })
    // Every project's linked list carries its connection's status.
    void queryClient.invalidateQueries({ queryKey: ['project'] })
    return true
  }
  if (type === PROJECT_REPOSITORIES_FRAME) {
    const projectId =
      typeof payload === 'object' &&
      payload !== null &&
      typeof (payload as Record<string, unknown>).projectId === 'string'
        ? ((payload as Record<string, unknown>).projectId as string)
        : null
    void queryClient.invalidateQueries({
      queryKey: projectId ? projectRepositoriesKey(projectId) : ['project'],
    })
    void queryClient.invalidateQueries({ queryKey: projectId ? ['board', projectId] : ['board'] })
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    void queryClient.invalidateQueries({ queryKey: DEEP_LINK_CARD_QUERY_PREFIX })
    // Connection rows carry projectCount; repository rows carry usedBy.
    void queryClient.invalidateQueries({ queryKey: CODE_CONNECTIONS_KEY })
    void queryClient.invalidateQueries({ queryKey: CODE_REPOSITORIES_ROOT })
    return true
  }
  return false
}
